/**
 * The Council (§9, §15.2 #7–8): discussion, nominations (tap to nominate or second), defence
 * (spotlight on the current nominee), the ballot (nominees + No exile; the Magistrate's Gavel;
 * a Ghost's ballot), then the simultaneous reveal with names and the result. A Grand Council
 * (§9.1) has a replacement window and a second ballot.
 *
 * Every permission comes from the view (`view.council`); the client decides nothing.
 */
import { useState } from "react";
import { PlayerChip } from "../components/PlayerChip.tsx";
import { BALLOT_OUTCOMES } from "../lib/labels.ts";
import { send } from "../lib/store.ts";
import { living, nameOf, playerOf, useView } from "../lib/view.ts";
import type { BallotReveal, PublicView } from "../lib/types.ts";
import { BulletinCard, DispatchCard } from "./Dusk.tsx";

export function Council() {
  const view = useView();
  const phase = view.public.phase;
  const round = phase.round;
  const bulletin = view.public.bulletins.find((b) => b.round === round);
  const earlier = view.public.revealedBallots.filter((b) => b.round === round);
  return (
    <div className="stack" data-testid={`council-${phase.kind}`}>
      {!view.me.alive ? (
        <p className="panel center muted" data-testid="ghost-note">
          You are a Ghost: you cannot nominate or be nominated.
          {view.me.ghostBallotsLeft === null
            ? " Your Ghost ballots are unlimited."
            : view.me.ghostBallotsLeft > 0
              ? " You still hold your Ghost ballot."
              : " Your Ghost ballot is spent."}
        </p>
      ) : null}
      {earlier.map((b) => (
        <section className="panel" key={`${b.round}-${b.number}`}>
          <BallotResult reveal={b} pub={view.public} />
        </section>
      ))}
      {phase.kind === "discussion" ? <Discussion /> : null}
      {phase.kind === "nominations" || phase.kind === "replacement" ? <Nominations /> : null}
      {phase.kind === "defence" ? <Defence /> : null}
      {phase.kind === "ballot" ? <BallotScreen /> : null}
      {phase.kind === "discussion" ? (
        <>
          {bulletin ? <BulletinCard bulletin={bulletin} pub={view.public} /> : null}
          <DispatchCard dispatch={view.dispatches.find((d) => d.round === round)} round={round} />
        </>
      ) : null}
    </div>
  );
}

function Discussion() {
  return (
    <div className="hero" style={{ padding: "0.5rem" }}>
      <h2>The Council gathers</h2>
      <p className="muted">
        Discuss freely: claim, accuse, lie. Nominations open when the timer ends.
      </p>
    </div>
  );
}

function Nominations() {
  const view = useView();
  const phase = view.public.phase;
  const window = phase.kind === "replacement" ? 2 : 1;
  const me = view.council;
  const noms = view.public.nominations.filter((n) => !n.withdrawn);
  const current = noms.filter((n) => n.window === window);
  const nominated = new Set(
    noms.filter((n) => n.window === window || n.forwardIndex !== null).map((n) => n.nominee),
  );
  const candidates = living(view.public.roster).filter((p) => !nominated.has(p.id));
  return (
    <>
      <section className="panel">
        <h3>{window === 2 ? "Replacement nominations" : "Nominations"}</h3>
        <p className="faint">
          A nomination stands once a different player seconds it. Each guest may nominate once and
          second once.
        </p>
        {noms.length === 0 ? <p className="muted">No nominations yet.</p> : null}
        <ul className="list" data-testid="nominations">
          {noms.map((n) => (
            <li key={`${n.window}-${n.nominee}`} style={{ flexWrap: "wrap" }}>
              <PlayerChip player={playerOf(view, n.nominee)} />
              <span className="grow faint">
                by {nameOf(view, n.nominator)}
                {n.seconder ? ` · seconded by ${nameOf(view, n.seconder)}` : ""}
              </span>
              {n.forwardIndex !== null ? (
                <span className="tag">Nominee</span>
              ) : me?.canSecond && n.nominator !== view.me.id && n.window === window ? (
                <button
                  type="button"
                  className="btn small primary"
                  data-second={nameOf(view, n.nominee)}
                  onClick={() => void send({ type: "second", nominee: n.nominee })}
                >
                  Second
                </button>
              ) : (
                <span className="tag plain">Needs a second</span>
              )}
            </li>
          ))}
        </ul>
        {current.length === 0 && noms.length > 0 ? (
          <p className="faint">No nominations in this window yet.</p>
        ) : null}
      </section>
      {me?.canNominate ? (
        <section className="panel">
          <h3>Nominate</h3>
          <ul className="list">
            {candidates.map((p) => (
              <li key={p.id}>
                <PlayerChip player={p} />
                <span className="grow" />
                <button
                  type="button"
                  className="btn small"
                  data-nominate={p.name}
                  onClick={() => void send({ type: "nominate", nominee: p.id })}
                >
                  Nominate
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function forwardedNominees(view: ReturnType<typeof useView>, window: 1 | 2): string[] {
  return view.public.nominations
    .filter((n) => n.window === window && n.forwardIndex !== null && !n.withdrawn)
    .sort((a, b) => (a.forwardIndex ?? 0) - (b.forwardIndex ?? 0))
    .map((n) => n.nominee);
}

function Defence() {
  const view = useView();
  const defending = view.public.phase.defending;
  const nominees = forwardedNominees(view, 1);
  return (
    <>
      <section className="panel spotlight center" data-testid="defence">
        <div className="kicker">Defence</div>
        <PlayerChip player={playerOf(view, defending)} big />
        <p className="muted" style={{ marginTop: "0.75rem" }}>
          {defending === view.me.id
            ? "The floor is yours. Make your defence."
            : "Listen to the defence."}
        </p>
      </section>
      <section className="panel">
        <h3>Nominees</h3>
        <ul className="list">
          {nominees.map((id) => (
            <li key={id}>
              <PlayerChip player={playerOf(view, id)} />
              {id === defending ? <span className="tag">speaking</span> : null}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function BallotScreen() {
  const view = useView();
  const progress = view.public.ballotProgress;
  const me = view.council;
  const [gavel, setGavel] = useState(false);
  if (!progress) return null;
  const mine = me?.myBallot ?? null;
  const ghost = !view.me.alive;
  const vote = (choice: string) =>
    void send({
      type: "castBallot",
      choice,
      gavel: gavel && me?.canGavel ? true : undefined,
      ghost: ghost ? true : undefined,
    });
  const options = [...progress.nominees, "none"];
  return (
    <section className="panel stack" data-testid="ballot">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>
          {progress.number === 2 ? "Second ballot" : "Ballot"}
        </h3>
        <span className="tag plain" data-testid="ballot-progress">
          {progress.cast} / {progress.living} ballots in
        </span>
      </div>
      {me?.canVote ? (
        <>
          <p className="faint" style={{ margin: 0 }}>
            {ghost
              ? "Cast your Ghost ballot: it is spent only if it still stands when the ballots are revealed."
              : "Choose one nominee or No exile. You can change your mind until the reveal."}
          </p>
          {options.map((choice) => (
            <button
              key={choice}
              type="button"
              className="vote-row"
              aria-pressed={mine?.choice === choice}
              data-vote={choice === "none" ? "none" : nameOf(view, choice)}
              onClick={() => vote(choice)}
            >
              {choice === "none" ? (
                <strong>No exile</strong>
              ) : (
                <PlayerChip player={playerOf(view, choice)} />
              )}
              <span className="grow" />
              {mine?.choice === choice ? (
                <span className="tag">{mine.gavel ? "Your Gavel" : "Your ballot"}</span>
              ) : null}
            </button>
          ))}
          {me.canGavel ? (
            <label className="row" style={{ minHeight: "var(--tap)" }}>
              <input
                type="checkbox"
                checked={gavel}
                onChange={(e) => setGavel(e.target.checked)}
                style={{ width: "1.4rem", height: "1.4rem" }}
              />
              <span>
                Cast as the Magistrate's <strong>Gavel</strong>: counts 2 and is shown publicly as
                yours.
              </span>
            </label>
          ) : null}
        </>
      ) : (
        <p className="muted">
          {ghost ? "Your Ghost ballot is spent. Watch the vote." : "You have no ballot here."}
        </p>
      )}
    </section>
  );
}

/** A revealed ballot: every ballot with names (open ballots), the tally and the result. */
export function BallotResult({
  reveal,
  pub,
  compact = false,
}: {
  reveal: BallotReveal;
  pub: PublicView;
  compact?: boolean;
}) {
  const view = pub;
  const choiceName = (c: string) => (c === "none" ? "No exile" : nameOf(view, c));
  return (
    <div className="stack" data-testid="ballot-result">
      <div>
        <div className="kicker">
          {reveal.number === 2 ? "Second ballot" : "Ballot"} · Round {reveal.round}
        </div>
        <strong style={{ fontSize: "1.15rem" }}>
          {reveal.exiled ? (
            <>{nameOf(view, reveal.exiled)} is exiled.</>
          ) : (
            BALLOT_OUTCOMES[reveal.outcome]
          )}
        </strong>
        <div className="faint">
          {reveal.tally.map((t) => `${choiceName(t.choice)} ${t.votes}`).join(" · ")} · needed{" "}
          {reveal.threshold}
        </div>
      </div>
      {!compact && reveal.ballots.length > 0 ? (
        <ul className="list">
          {reveal.ballots.map((b, i) => (
            <li key={i}>
              {b.voter ? <PlayerChip player={playerOf(view, b.voter)} /> : <span>A voter</span>}
              <span className="faint">→</span>
              <span className="grow">{choiceName(b.choice)}</span>
              {b.gavel ? <span className="tag">Gavel ×2</span> : null}
              {b.ghost ? <span className="tag plain">Ghost</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
